import { apiService } from './ApiService';

// Dashboard types
interface DashboardStats {
  total_requests: number;
  total_conversations: number;
  total_users: number;
  total_channels: number;
  requests_change: number;
  conversations_change: number;
  users_change: number;
  channels_change: number;
  daily_stats: Array<{
    date: string;
    requests: number;
  }>;
}

// MongoDB response format
interface MongoDBDashboardResponse {
  total_requests: number;
  total_conversations: number;
  total_users: number;
  total_channels: number;
  requests_change: number;
  conversations_change: number;
  users_change: number;
  channels_change: number;
  trend_data: Array<{
    date: string;
    requests: number;
  }>;
}

class DashboardService {
  // Get dashboard statistics
  async getStats(days?: number, startDate?: string, endDate?: string): Promise<DashboardStats> {
    try {
      const params: any = {};
      
      if (days) params.days = days;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const startTime = performance.now();
      const response = await apiService.get<MongoDBDashboardResponse>('/api/v1/mongodb/dashboard/stats', params);     
      const endTime = performance.now();
      
      if (response.data) {
        // Map MongoDB response to frontend format
        const mongoResponse = response.data;
        return {
          total_requests: mongoResponse.total_requests,
          total_conversations: mongoResponse.total_conversations,
          total_users: mongoResponse.total_users,
          total_channels: mongoResponse.total_channels,
          requests_change: mongoResponse.requests_change,
          conversations_change: mongoResponse.conversations_change,
          users_change: mongoResponse.users_change,
          channels_change: mongoResponse.channels_change,
          daily_stats: mongoResponse.trend_data || [] // Map trend_data to daily_stats
        };
      }
      
      throw new Error(response.error || 'Failed to fetch dashboard stats');
    } catch (error) {
      throw error;
    }
  }

  // Get system health
  async getHealth(): Promise<any> {
    try {
      const response = await apiService.get('/api/health');
      return response.data;
    } catch (error) {
      console.error('Health check error:', error);
      throw error;
    }
  }

  // Get system metrics
  async getMetrics(): Promise<any> {
    try {
      const response = await apiService.get('/api/health/metrics');
      return response.data;
    } catch (error) {
      console.error('Metrics error:', error);
      throw error;
    }
  }

  // Export conversations to Excel
  async exportConversations(days?: number, startDate?: string, endDate?: string): Promise<Blob> {
    try {
      const params: any = {};
      
      if (days) params.days = days;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await apiService.getBlob('/api/v1/mongodb/dashboard/export/conversations', params);
      return response;
    } catch (error) {
      console.error('Export MongoDB conversations error:', error);
      throw error;
    }
  }

  // Export user requests to Excel
  async exportUserRequests(days?: number, startDate?: string, endDate?: string): Promise<Blob> {
    try {
      const params: any = {};
      
      if (days) params.days = days;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await apiService.getBlob('/api/v1/mongodb/dashboard/export/requests', params);
      return response;
    } catch (error) {
      console.error('Export MongoDB user requests error:', error);
      throw error;
    }
  }

  // Export new users to Excel
  async exportNewUsers(days?: number, startDate?: string, endDate?: string): Promise<Blob> {
    try {
      const params: any = {};
      
      if (days) params.days = days;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      console.log('🚀 DashboardService - exportNewUsers request:', { params });
      const response = await apiService.getBlob('/api/v1/mongodb/dashboard/export/new-users', params);
      console.log('✅ DashboardService - exportNewUsers response received');
      return response;
    } catch (error) {
      console.error('❌ Export MongoDB new users error:', error);
      throw error;
    }
  }
}

export const dashboardService = new DashboardService();
export default DashboardService;
